import { Classification, Feature } from '@telialt/models/src/EshopModel';

import { ProductCompareClassification, ProductCompareValue } from './models';

const flatUniqueList = <T extends { code: string }>(lists: T[][]): T[] => {
    return lists
        .filter(Boolean)
        .sort((a, b) => b.length - a.length) // moving largest list to the top
        .flat()
        .reduce<T[]>((acc, item) => {
            if (!acc.some(({ code }) => code === item.code)) {
                acc.push(item);
            }

            return acc;
        }, []);
};

const formatStringValue = (value: string) => {
    const lowerCaseValue = value?.trim().toLowerCase();
    if (lowerCaseValue === 'true' || lowerCaseValue === 'taip') {
        return true;
    } else if (lowerCaseValue === 'false' || lowerCaseValue === 'ne' || lowerCaseValue === '-') {
        return false;
    } else {
        return value ?? '';
    }
};

const formatFeature = (feature?: Feature): ProductCompareValue => {
    if (!feature) {
        return null;
    }

    const formattedValues = feature.featureValues.map(({ value }) => formatStringValue(value));

    const unitSymbol = feature.featureUnit?.symbol;

    if (feature.range) {
        const joinedValues = formattedValues.join(' - ');

        return [joinedValues, unitSymbol].filter(Boolean).join(' ');
    }

    const formattedValuesWithUnit = unitSymbol
        ? formattedValues.map((value) => [value, unitSymbol].join(' '))
        : formattedValues;

    if (formattedValuesWithUnit.length === 1) {
        return formattedValuesWithUnit[0];
    }

    return formattedValuesWithUnit;
};

const getProductsFeatureValues = (featureCode: string, features: Record<string, Feature[]>) => {
    return Object.keys(features).reduce<Record<string, ProductCompareValue>>((acc, productCode) => {
        const productFeature = features[productCode]?.find((feature) => feature.code === featureCode);

        return { ...acc, [productCode]: formatFeature(productFeature) };
    }, {});
};

const checkAllValuesEqual = (values: ProductCompareValue[]) => {
    return values.reduce<boolean>((allEqual, value, index) => {
        if (index === 0 || !allEqual) {
            return allEqual;
        }
        const previousValue = values[index - 1];
        if (typeof value !== typeof previousValue) {
            return false;
        }
        if (Array.isArray(value) && Array.isArray(previousValue)) {
            return value.every((entry) => previousValue.includes(entry));
        }

        return value === previousValue;
    }, true);
};

const mergeProductsFeatures = (features: Record<string, Feature[]>) => {
    const uniqueFeatures = flatUniqueList(Object.values(features));

    return uniqueFeatures
        .filter((feature) => feature.comparable)
        .map((feature) => {
            const code = feature.code;
            const name = feature.name;
            const values = getProductsFeatureValues(code, features);
            const isEqual = checkAllValuesEqual(Object.values(values));

            return {
                code,
                name,
                values,
                isEqual,
            };
        });
};

export const compareProductsClassifications = (
    classifications: Record<string, Classification[] | undefined>,
): ProductCompareClassification[] => {
    const uniqueClassifications = flatUniqueList(Object.values(classifications));

    const mergedList = uniqueClassifications.map((classification) => {
        const code = classification.code;
        const name = classification.name;
        const mappedProductsFeatures = Object.keys(classifications).reduce<Record<string, Feature[]>>(
            (acc, productCode) => {
                const productFeatures = classifications[productCode].find((item) => item.code === code)?.features;

                return { ...acc, [productCode]: productFeatures };
            },
            {},
        );
        const mergedFeatures = mergeProductsFeatures(mappedProductsFeatures);

        return {
            code,
            name,
            features: mergedFeatures,
        };
    });

    return mergedList;
};

export const filterOnlyDifferentFeatures = (
    classifications: ProductCompareClassification[],
): ProductCompareClassification[] => {
    return classifications.reduce((acc, classification) => {
        const differentFeatures = classification.features.filter((feature) => !feature.isEqual);
        if (!differentFeatures.length) {
            return acc;
        }

        return [...acc, { ...classification, features: differentFeatures }];
    }, []);
};
